import type { FormMode, IdentityFormData } from "@/screen/Identities/Interfaces/IdentityFormInterfaces";
import { parseIsoDate } from "@/utils/DateFormatter";

// Sustitución del documento en edición: se permite renovar (subir anverso/reverso nuevos sin re-analizar)
// en ACTIVE, OUTDATED y PENDING, y en NEW cuando el documento persistido está caducado. Es la vía para
// desbloquear a un interviniente con el documento caducado, que el backend rechaza editar salvo que se
// aporten imágenes nuevas y una nueva fecha de validez.
const IdentityDocumentRenewalStatuses = ["ACTIVE", "OUTDATED", "PENDING"];

export interface IdentityDocumentDetail
{
    status?: string | null;
    hasDniFront?: boolean;
    hasDniBack?: boolean;
    /** Fecha de validez PERSISTIDA: es la que evalúa la guarda de documento caducado del backend. */
    idExpiration?: string | null;
    hasPermanentIdExpiration?: boolean | null;
}

type IdentityDocumentFormData = Pick<IdentityFormData, "idFront" | "idBack">;

export interface IdentityDocumentAccess
{
    /** Al interviniente le falta el anverso, el reverso o ambos. */
    isMissingIdentityDocuments: boolean;
    /** Habilita los inputs de fichero del DNI y enruta la subida por la rama que no dispara el análisis. */
    canRenewIdentityDocument: boolean;
    /** Anverso y reverso persistidos en BD, aunque no se hayan podido recuperar las previews. */
    hasStoredIdentityDocuments: boolean;
    /** La fecha de validez PERSISTIDA está caducada, con el mismo criterio que la guarda del backend.
     *  Solo decide el texto que acompaña a la subida: no habilita ni bloquea nada por sí mismo. */
    isStoredDocumentExpired: boolean;
    /** No hay fecha de validez PERSISTIDA (ni validez permanente): el usuario debe rellenarla para guardar.
     *  Como `isStoredDocumentExpired`, solo decide el texto que acompaña a la subida. */
    isStoredIdExpirationMissing: boolean;
}

export function resolveIdentityDocumentAccess(mode: FormMode, loadedIdentityDetail: IdentityDocumentDetail | null, formData: IdentityDocumentFormData): IdentityDocumentAccess
{
    const isEditingLoadedIdentity = mode === "edit" && loadedIdentityDetail !== null;
    const status = String(loadedIdentityDetail?.status ?? "").trim().toUpperCase();
    const hasIdFront = loadedIdentityDetail?.hasDniFront === true || !!formData.idFront;
    const hasIdBack = loadedIdentityDetail?.hasDniBack === true || !!formData.idBack;

    // Los borradores creados fuera de este formulario (el principal nace en la contratación, sin pasar
    // por el orquestador) llegan sin imágenes: sin habilitar la subida no habría ninguna vía para
    // aportarlas y el alta quedaría muerta. Basta con que falte una para reclamar las dos, porque el
    // backend borra la cara que no se reenvía cuando ChangedDocuments.Dni viaja a true.
    const isMissingIdentityDocuments = isEditingLoadedIdentity && (!hasIdFront || !hasIdBack);

    // Un borrador NEW con ambas imágenes pero con el documento persistido caducado quedaba sin salida:
    // la guarda de caducidad (isExpiredDocumentRenewalIncomplete) exigía anverso y reverso nuevos para
    // guardar, pero los inputs de fichero seguían deshabilitados por no estar en un estado de renovación.
    const hasExpiredStoredDocument = isEditingLoadedIdentity && isStoredDocumentExpired(loadedIdentityDetail);
    const isExpiredDocumentOnNewDraft = status === "NEW" && hasExpiredStoredDocument;

    return{
        isMissingIdentityDocuments,
        canRenewIdentityDocument: isEditingLoadedIdentity && status !== "EXTERNAL" &&
            (IdentityDocumentRenewalStatuses.includes(status) || isMissingIdentityDocuments || isExpiredDocumentOnNewDraft),
        hasStoredIdentityDocuments: isEditingLoadedIdentity && hasIdFront && hasIdBack,
        isStoredDocumentExpired: hasExpiredStoredDocument,
        isStoredIdExpirationMissing: isEditingLoadedIdentity && isStoredIdExpirationMissing(loadedIdentityDetail),
    };
}

// ─── Documento caducado: espejo en cliente de la guarda del backend ──────────

function toStartOfDay(value: Date): Date
{
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

/**
 * Indica si la fecha de validez PERSISTIDA está caducada, con el mismo criterio que el backend
 * (`IdExpiration <= today`: el propio día de caducidad ya no es válido). La validez permanente nunca
 * caduca (su centinela es 9999-01-01) y sin fecha persistida no hay nada que evaluar.
 */
function isStoredDocumentExpired(loadedIdentityDetail: IdentityDocumentDetail | null): boolean
{
    if(loadedIdentityDetail === null || loadedIdentityDetail.hasPermanentIdExpiration === true)
    {
        return false;
    }

    const storedIdExpiration = parseIsoDate(loadedIdentityDetail.idExpiration);
    if(storedIdExpiration === null)
    {
        return false;
    }

    return toStartOfDay(storedIdExpiration).getTime() <= toStartOfDay(new Date()).getTime();
}

/**
 * Indica que el interviniente no tiene fecha de validez PERSISTIDA válida. La validez permanente cuenta
 * como fecha informada aunque no llegue `idExpiration`.
 */
function isStoredIdExpirationMissing(loadedIdentityDetail: IdentityDocumentDetail | null): boolean
{
    if(loadedIdentityDetail === null || loadedIdentityDetail.hasPermanentIdExpiration === true)
    {
        return false;
    }

    return parseIsoDate(loadedIdentityDetail.idExpiration) === null;
}

/**
 * Espejo de la guarda ID_DOCUMENT_EXPIRED_RENEWAL_REQUIRED del orquestador: cuando la fecha de validez
 * PERSISTIDA está caducada, el backend solo acepta el guardado si llegan anverso y reverso nuevos —los
 * exige a la vez— junto a una fecha futura.
 *
 * Anticiparlo en cliente evita el viaje al servidor y, sobre todo, un mensaje contradictorio: la guarda
 * evalúa la fecha almacenada, no la del formulario, así que el usuario leía "documento caducado" con una
 * fecha de validez futura recién escrita en pantalla.
 *
 * La fecha del formulario no se comprueba aquí: de eso ya se encarga `validation.idExpiration`.
 */
export function isExpiredDocumentRenewalIncomplete(loadedIdentityDetail: IdentityDocumentDetail | null, formData: IdentityDocumentFormData): boolean
{
    if(!isStoredDocumentExpired(loadedIdentityDetail))
    {
        return false;
    }

    // El backend pide las dos caras a la vez (hasNewDniFiles): sustituir solo una no renueva el documento.
    return !(formData.idFront instanceof File && formData.idBack instanceof File);
}
